// 1. **Dynamic Banner Rendering**: Displays banners dynamically based on the provided `bannerData`.
// 2. **Responsive Design**: Adapts content to desktop or mobile screens using `useMediaQuery`.
// 3. **Date Range Filtering**: Ensures banners are shown only if their date range is valid.
// 4. **Media Handling**: Supports images, videos, and text, rendering the appropriate media type per screen size.
// 5. **Overlay Text**: Displays overlay content, such as titles or descriptions, tailored for the screen type.
// 6. **Call-to-Action Buttons**: Includes interactive buttons with dynamic styles based on banner data.
// 7. **Utility Functions**: Uses helper functions to validate image URLs and determine date range eligibility.

import useMediaQuery from 'helpers/hooks/useMediaQuery';
import React, { useEffect, useMemo, useState } from 'react';
import { desktop, tablet, mobile, xxs } from 'helpers/utils/screensizes';
import { ContentLink, DataHomepage, Media, Overlay, OverlayContent, OverlayText } from 'types/next';
import Link from 'components/commercetools-ui/atoms/link';
import useLocale from 'helpers/hooks/useLocale';
import { usePathname } from 'next/navigation';
import { useAccount, useCart } from 'frontastic';
import { DataLayerHelpers, DataLayerOnLoad } from 'helpers/dataLayerHelpers';
interface bannerData {
  bannerData: DataHomepage;
}

const HomePageBanner = (bannerData: bannerData) => {
  const banners = bannerData?.bannerData?.data?.data?.dataSource?.contentLinks;
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isTablet] = useMediaQuery(tablet);
  const [ismobile] = useMediaQuery(xxs);
  const [currentPositionClass, setCurrentPositionClass] = useState<string>('');

  const isWithinDateRange = (startDate: string, endDate: string) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    return now >= start && now <= end;
  };

  const { account } = useAccount();
  const { getCountry, getCurrency } = useLocale();
  const localeData = {
    country: getCountry(),
    currency: getCurrency(),
  };
  const selectedLocale = getCurrency() === 'GBP' ? 'en-GB' : 'en-EU';

  const getPositionClass = (position: string) => {
    const positionMap: Record<string, string> = {
      'top-left': 'top-0 left-0',
      'top-center': 'top-0 left-1/2 -translate-x-1/2',
      'top-right': 'top-0 right-0',
      'center-left': 'top-1/2 left-0 -translate-y-1/2',
      'center-center': 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
      'center-right': 'top-1/2 right-0 -translate-y-1/2',
      'bottom-left': 'bottom-0 left-0',
      'bottom-center': 'bottom-0 left-1/2 -translate-x-1/2',
      'bottom-right': 'bottom-0 right-0',
      top: 'top-0 left-1/2 -translate-x-1/2',
      bottom: 'bottom-0 left-1/2 -translate-x-1/2',
      center: 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
    };
    return positionMap?.[position] || '';
  };

  //Accoridan Renderers
  const AccordionReadMore = ({
    text,
    color,
    fontSize,
    readMoreColor,
    readMoreSize,
    alignment,
  }: {
    text: string | undefined;
    color: string | undefined;
    fontSize: number | undefined;
    readMoreColor: string | undefined;
    readMoreSize: number | undefined;
    alignment: string | undefined;
  }) => {
    const [expanded, setExpanded] = useState(false);

    // Split into sentences and show max 2 by default
    const sentences = text?.match(/[^.!?]+[.!?]+/g) || [text];
    const previewText = sentences?.slice(0, 2).join(' ').trim();
    const isLong = sentences?.length > 2;
    let justifyContent;

    if (alignment === 'text-left') {
      justifyContent = 'justify-start';
    } else if (alignment === 'text-center') {
      justifyContent = 'justify-center';
    } else if (alignment === 'text-right') {
      justifyContent = 'justify-end';
    }

    return (
      <div className="">
        <p
          className="font-body"
          style={{
            color,
            fontSize: fontSize ? `${fontSize}px` : '20px',
          }}
        >
          {expanded || !isLong ? text : `${previewText}`}
        </p>
        {isLong && (
          <div className={`flex ${justifyContent}`}>
            <button
              className="mt-2 flex items-center gap-2 font-semibold"
              style={{
                color: readMoreColor ? readMoreColor : 'white',
                fontSize: readMoreSize ? `${readMoreSize}px` : '20px',
              }}
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? (
                <>
                  Read Less
                  <svg
                    width="18"
                    height="18"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    className="rotate-180 transition-transform duration-200"
                  >
                    <path d="M14.77 7.79a1 1 0 0 0-1.42 0L10 11.17 6.65 7.79a1 1 0 1 0-1.42 1.42l4.06 4.06a1 1 0 0 0 1.42 0l4.06-4.06a1 1 0 0 0 0-1.42z" />
                  </svg>
                </>
              ) : (
                <>
                  Read More
                  <svg
                    width="18"
                    height="18"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    className="transition-transform duration-200"
                  >
                    <path d="M14.77 7.79a1 1 0 0 0-1.42 0L10 11.17 6.65 7.79a1 1 0 1 0-1.42 1.42l4.06 4.06a1 1 0 0 0 1.42 0l4.06-4.06a1 1 0 0 0 0-1.42z" />
                  </svg>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Media renderers
  const renderImage = (imageData: Media[], altText: string, link?: string) => {
    const mobileImage = imageData?.find((m: Media) => m?.screentype === 'mobile' && m?.type === 'image');
    const tabImage = imageData?.find((m: Media) => m?.screentype === 'tablet' && m?.type === 'image');
    const desktopImage = imageData.find((m: Media) => m?.screentype === 'desktop' && m?.type === 'image');
    const imageElement = (
      <picture className="w-full">
        {mobileImage?.image?.[selectedLocale] && (
          <source
            media="(max-width: 767px)"
            srcSet={`https://${mobileImage?.image?.[selectedLocale]?.defaultHost}/i/${mobileImage?.image?.[selectedLocale]?.endpoint}/${mobileImage?.image?.[selectedLocale]?.name}?fmt=webp`}
          />
        )}
        {tabImage?.image?.[selectedLocale] && (
          <source
            media="(min-width: 768px) and (max-width: 1023px)"
            srcSet={`https://${tabImage?.image?.[selectedLocale]?.defaultHost}/i/${tabImage?.image?.[selectedLocale]?.endpoint}/${tabImage?.image?.[selectedLocale]?.name}?fmt=webp`}
          />
        )}
        {desktopImage?.image?.[selectedLocale] && (
          <source
            media="(min-width: 1023px)"
            srcSet={`https://${desktopImage?.image?.[selectedLocale]?.defaultHost}/i/${desktopImage?.image?.[selectedLocale]?.endpoint}/${desktopImage?.image?.[selectedLocale]?.name}?fmt=webp`}
          />
        )}
        <img
          src={`https://${desktopImage?.image?.[selectedLocale]?.defaultHost}/i/${desktopImage?.image?.[selectedLocale]?.endpoint}/${desktopImage?.image?.[selectedLocale]?.name}?fmt=webp`}
          alt={altText}
          className="relative w-full object-cover"
        />
      </picture>
    );

    return link ? (
      <Link link={link} className="block h-full w-full cursor-pointer">
        {imageElement}
      </Link>
    ) : (
      imageElement
    );
  };

  useEffect(() => {
    // Calculate position only when banners or screen size changes
    if (banners && banners?.length > 0) {
      const position = isDesktopSize ? banners?.[0]?.desktopPosition || '' : banners?.[0]?.mobilePosition || '';
      setCurrentPositionClass(getPositionClass(position));
    }
  }, [banners, ismobile]);
  useEffect(() => {
    const currentUrl = document.location.origin;
    const dataLayer = new DataLayerOnLoad(currentUrl, '/', localeData, account);
    dataLayer.setDataLayerOnLoad();
  }, []);

  return (
    <div className="relative">
      {banners &&
        banners?.length > 0 &&
        banners?.map((banner: ContentLink, bannerIndex: number) => {
          const { dateRange } = banner?.filters;
          if (!isWithinDateRange(dateRange?.startDate, dateRange?.endDate) || !dateRange) {
            // If banner is expired means, we will show this default banner
            return (
              <>
                <Link link={'/sale'}>
                  <div className="default-banner relative flex h-[100%] w-[100%] flex-col items-center justify-center">
                    <img
                      src={`https://cdn.media.amplience.net/i/americangolf/blue-flag-event_2025_generic-ends-midnight_1960x560_desktop`}
                      alt="american golf hero banner"
                      title="american golf hero banner"
                      className="relative h-[100%] w-[100%]"
                    />
                  </div>
                </Link>
              </>
            );
          }
          const filteredMedia = banner?.media?.find((media: Media) =>
            isDesktopSize
              ? media?.screentype === 'desktop'
              : isTablet
              ? media?.screentype === 'tablet'
              : media?.screentype === 'mobile',
          );
          const filterOverlayText = banner?.overlayText?.find((overlay: OverlayText) =>
            ismobile ? overlay?.screentype === 'mobile' : overlay?.screentype === 'desktop',
          );

          const mediaImage = banner?.media?.find(
            (media: Media) =>
              media?.type === 'image' &&
              (isDesktopSize
                ? media?.screentype === 'desktop'
                : isTablet
                ? media?.screentype === 'tablet'
                : ismobile
                ? media?.screentype === 'mobile'
                : media?.screentype === 'desktop'),
          );

          const mediaText = banner?.media?.find(
            (media: Media) =>
              media?.type === 'text' &&
              (isDesktopSize
                ? media?.screentype === 'desktop'
                : isTablet
                ? media?.screentype === 'tablet'
                : ismobile
                ? media?.screentype === 'mobile'
                : media?.screentype === 'mobile'),
          );

          const mediaVideo = banner?.media?.find(
            (media: Media) =>
              media?.type === 'video' &&
              (isDesktopSize
                ? media?.screentype === 'desktop'
                : isTablet
                ? media?.screentype === 'tablet'
                : ismobile
                ? media?.screentype === 'mobile'
                : media?.screentype === 'desktop'),
          );

          const isValidImage =
            mediaImage?.image &&
            mediaImage?.image?.[selectedLocale]?.defaultHost &&
            mediaImage?.image?.[selectedLocale]?.endpoint &&
            mediaImage?.image?.[selectedLocale]?.name;

          const isValidVideo =
            mediaVideo?.video &&
            mediaVideo?.video?.[selectedLocale]?.defaultHost &&
            mediaVideo?.video?.[selectedLocale]?.endpoint &&
            mediaVideo?.video?.[selectedLocale]?.name;

          const overlayTextImage = banner?.overlayText?.find(
            (overlay: OverlayText) =>
              overlay?.type === 'image' &&
              (isDesktopSize
                ? overlay?.screentype === 'desktop'
                : isTablet
                ? overlay?.screentype === 'tablet'
                : ismobile
                ? overlay?.screentype === 'mobile'
                : overlay?.screentype === 'desktop'),
          );

          const overlayTextVideo = banner?.overlayText?.find(
            (overlay: OverlayText) =>
              overlay?.type === 'video' &&
              (isDesktopSize
                ? overlay?.screentype === 'desktop'
                : isTablet
                ? overlay?.screentype === 'tablet'
                : ismobile
                ? overlay?.screentype === 'mobile'
                : overlay?.screentype === 'desktop'),
          );
          const overlayTextText = banner?.overlayText?.find(
            (overlay: OverlayText) =>
              overlay?.type === 'text' &&
              (isDesktopSize
                ? overlay?.screentype === 'desktop'
                : isTablet
                ? overlay?.screentype === 'tablet'
                : ismobile
                ? overlay?.screentype === 'mobile'
                : overlay?.screentype === 'desktop'),
          );
          const altText = mediaImage?.altText?.[selectedLocale] || 'Banner image';

          return (
            <div
              key={bannerIndex}
              className="relative flex h-[100%] w-[100%] flex-col items-center justify-center"
              style={{
                backgroundColor: filteredMedia?.backgroundColour || 'transparent',
              }}
            >
              {isValidVideo ? (
                filteredMedia?.link ? (
                  <Link link={filteredMedia?.link} className="block h-full w-full cursor-pointer">
                    <video
                      src={`https://${mediaVideo?.video?.[selectedLocale]?.defaultHost}/v/${mediaVideo?.video?.[selectedLocale]?.endpoint}/${mediaVideo?.video?.[selectedLocale]?.name}/mp4_720p`}
                      loop
                      autoPlay
                      muted
                      playsInline
                      className="relative h-[100%] w-[100%] object-cover"
                    />
                  </Link>
                ) : (
                  <video
                    src={`https://${mediaVideo?.video?.[selectedLocale]?.defaultHost}/v/${mediaVideo?.video?.[selectedLocale]?.endpoint}/${mediaVideo?.video?.[selectedLocale]?.name}/mp4_720p`}
                    loop
                    autoPlay
                    muted
                    playsInline
                    className="relative h-[100%] w-[100%] object-cover"
                  />
                )
              ) : isValidImage ? (
                renderImage(banner?.media, altText, filteredMedia?.link)
              ) : // mediaText ? (
              //   <h1 className="text-center text-white">
              //     {mediaText?.text?.[selectedLocale]}
              //   </h1>
              // ) :
              null}
              {currentPositionClass && (
                <div className={`absolute ${currentPositionClass} min-h-[100px] w-full lg:min-h-[100px] xl:w-[1024px]`}>
                  <div className={`flex w-full flex-col gap-[1rem] p-[1rem] md:p-[1.5rem] lg:p-[2rem]`}>
                    {(overlayTextImage || overlayTextVideo || overlayTextText) && (
                      <div className="flex w-full max-w-[1024px] flex-col">
                        {overlayTextImage && (
                          <div className="flex w-full justify-center ">
                            {overlayTextImage?.overlayContent?.map((content: OverlayContent, index: number) => (
                              <img
                                key={index}
                                src={`https://${content?.overlayImage?.[selectedLocale]?.endpoint}.a.bigcontent.io/v1/static/${content?.overlayImage?.[selectedLocale]?.name}`}
                                // src={`https://${content?.overlayImage?.[selectedLocale]?.defaultHost}/i/${content?.overlayImage?.[selectedLocale]?.endpoint}/${content?.overlayImage?.[selectedLocale]?.name}`}
                                alt="overlay image"
                                className={`mb-2 ${
                                  content?.overlayImage?.height ? `h-[${content?.overlayImage?.height}px]` : 'h-[150px]'
                                }`}
                              />
                            ))}
                          </div>
                        )}

                        {overlayTextVideo && (
                          <div className="flex w-full max-w-[150px] justify-center sm:max-w-[300px]">
                            {overlayTextVideo?.overlayContent?.map((content: OverlayContent, index: number) => (
                              <video
                                key={index}
                                src={`https://${content?.overlayVideo?.[selectedLocale]?.defaultHost}/v/${content?.overlayVideo?.[selectedLocale]?.endpoint}/${content?.overlayVideo?.[selectedLocale]?.name}/mp4_720p`}
                                autoPlay
                                loop
                                muted
                                playsInline
                                className="mb-2 w-[100%]"
                              />
                            ))}
                          </div>
                        )}

                        {overlayTextText && (
                          <>
                            {overlayTextText?.overlayContent?.map((content: OverlayContent, index: number) => {
                              const paragraphData = content?.textContent?.paragraph;
                              const paragraphText = paragraphData
                                ? paragraphData?.[selectedLocale]?.introParagraph
                                : '';
                              const enableReadMore = paragraphData?.[selectedLocale]?.enableReadMore;
                              const headingTag = content?.textContent?.heading?.htmlTag || 'h2';
                              const fontWeight = content?.textContent?.heading?.fontWeight || '600';
                              // Create the heading element dynamically based on the selected HTML tag
                              const HeadingTag = headingTag as keyof JSX.IntrinsicElements;

                              return (
                                <div className={`flex flex-col ${content?.textAlignmentPosition}`} key={index}>
                                  <HeadingTag
                                    className="pb-[0.5rem] font-body font-bold leading-none md:pb-[0.25rem]"
                                    key={index}
                                    style={{
                                      color: content?.textContent?.heading?.textColour,
                                      fontSize: content?.textContent?.heading?.textSize
                                        ? `${content?.textContent?.heading?.textSize}px`
                                        : '20px',
                                      fontWeight: fontWeight,
                                    }}
                                  >
                                    {content?.textContent?.heading?.[selectedLocale]}
                                  </HeadingTag>
                                  {enableReadMore ? (
                                    <AccordionReadMore
                                      text={paragraphText}
                                      color={paragraphData?.textColour}
                                      readMoreColor={paragraphData?.readMoreColour}
                                      readMoreSize={paragraphData?.textSize}
                                      fontSize={paragraphData?.textSize}
                                      alignment={content?.textAlignmentPosition}
                                    />
                                  ) : (
                                    <p
                                      className="font-body"
                                      style={{
                                        color: paragraphData?.textColour,
                                        fontSize: paragraphData?.textSize ? `${paragraphData?.textSize}px` : '20px',
                                      }}
                                    >
                                      {paragraphText}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </>
                        )}
                      </div>
                    )}
                    <div
                      className={`
    grid
    w-full
    gap-[1rem]
    text-center
    ${isDesktopSize ? 'max-w-[1024px] grid-cols-4' : ''}
    ${isTablet ? (banner?.overlay?.length > 4 ? 'grid-cols-4' : 'grid-cols-4') : ''}
    ${ismobile && banner?.overlay?.length === 1 ? 'grid-cols-1' : ismobile ? 'grid-cols-2' : ''}
  `}
                    >
                      {banner &&
                        banner?.overlay?.length > 0 &&
                        banner?.overlay?.map((button: Overlay, index: number) => {
                          const isTabletSix =
                            !isDesktopSize && isTablet && banner?.overlay?.length === 6 && (index === 4 || index === 5);
                          return (
                            <a
                              href={button?.link || '#'}
                              key={index}
                              target={button?.linkTarget === '_blank' ? '_blank' : '_self'}
                              rel={button?.linkTarget === '_blank' ? 'noopener noreferrer' : undefined}
                              onClick={(e) => {
                                if (button?.linkTarget === '_newwindow') {
                                  e.preventDefault();
                                  window.open(button.link, '_blank', 'noopener,noreferrer,width=800,height=600');
                                }
                              }}
                              className={`w-full ${isTabletSix ? 'col-span-2' : ''}`}
                            >
                              <button
                                className={`
            flex h-[40px] w-full items-center justify-center
  whitespace-normal rounded-sm px-2 text-center
  text-14 font-bold uppercase text-black
  lg:text-16
           
          `}
                                style={{
                                  backgroundColor: button?.buttonBackgroundColour,
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = button?.HoverButtonBackgroundColour;
                                  e.currentTarget.style.color = button?.hoverButtonTextColour;
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = button?.buttonBackgroundColour;
                                  e.currentTarget.style.color = 'black'; // Default text color
                                }}
                              >
                                {button?.text?.[selectedLocale]}
                              </button>
                            </a>
                          );
                        })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};

export default HomePageBanner;
